import { Dispatch } from 'react';

import {
	ApiMap,
	Instances,
	SubaccountInstances,
	LoginInstance,
} from '../../constants';
import { IObjectField } from '../../interface';
import { apiBaseUrl, networkCall } from '../../utils/networkCall';
import { convertFilterToUrlParams } from '../../utils/storeUtility';

import { IStateType } from '../models/root.interface';
import { makeSelectRequestParams } from './selectors';

import { instanceStoreRecord, InstanceType } from './store';
import { fbCustomHeaders } from '../../pages/Fallback/constants';

export const OBJECT_REQUESTED = 'object/OBJECT_REQUESTED';
export const OBJECT_SUCCEEDED = 'object/OBJECT_SUCCEEDED';
export const OBJECT_FAILED = 'object/OBJECT_FAILED';
export const FILTERS_CHANGED = 'object/FILTERS_CHANGED';

// local object manuplation
export const LOCAL_CHANGE_OBJECT_FIELD_REQUESTED =
	'object/LOCAL_CHANGE_OBJECT_FIELD_REQUESTED';
export const LOCAL_CREATE_INITIAL_OBJECT = 'object/LOCAL_CREATE_INITIAL_OBJECT';
export const REMOVE_OBJECT = 'object/REMOVE_OBJECT';

const getUrl = (
	instanceId: Instances,
	defaultInstance: Partial<InstanceType> = {},
	id: any,
	apiName?: string,
	dynamicUrlData?: { [key: string]: string }
): string => {
	const instance = instanceStoreRecord(defaultInstance).toJS();

	const { requestParams, filters } = instance;

	const apiFn = ApiMap[instanceId];
	let url = apiFn;
	if (dynamicUrlData) {
		url = url.replace(`:Id`, dynamicUrlData["Id"]);
	}
	if (typeof apiFn === 'function') {
		url = (apiFn as Function)(requestParams);
	}

	if (id) {
		url = `${url}/${id}`;
	}

	if (filters) {
		const urlParams = new URLSearchParams();
		convertFilterToUrlParams(urlParams, filters);
		return `${url}${url.includes('?') ? '&' : '?'}${urlParams.toString()}`;
	}

	if (requestParams) {
		const urlParams = new URLSearchParams();
		convertFilterToUrlParams(urlParams, requestParams);
		return `${url}${url.includes('?') ? '&' : '?'}${urlParams.toString()}`;
	}

	return url;
};

const makeAPIcall = (
	dispatch: Dispatch<ObjectSucceededType | ObjectFailedType>,
	instanceId: Instances,
	defaultInstance: Partial<InstanceType> = {},
	type: string = 'get'!,
	includeId: boolean = true,
	header: boolean = true,
	data: object = undefined!,
	callBack?: any,
	id?: string,
	apiName?: string,
	apiService?: string,
	dynamicUrlData?: { [key: string]: string },
) => {
	let url = apiBaseUrl.baseUrl;
	const customHeaderFb = instanceId?.includes("Fallback") ?fbCustomHeaders : undefined;
	if (instanceId) {
		if (SubaccountInstances.includes(instanceId) || apiService === 'web-api') {
			url = apiBaseUrl.webApi;
		}
		if (LoginInstance.includes(instanceId)) {
			url = apiBaseUrl.parentLoginApi;
		}
		if (instanceId.includes("Fallback")) {
			url = apiBaseUrl.wmsApi;
		}
		if (apiService === "chatbotUrl") {
			url = apiBaseUrl.chatbotURL;
		}
		if(apiService === 'analyticsBeta'){
			url = apiBaseUrl.analyticsBeta
		}
		networkCall(
			type,
			getUrl(instanceId, defaultInstance, id, apiName, dynamicUrlData),
			url,
			includeId,
			header,
			data,
			instanceId,
			undefined,
			undefined,
			customHeaderFb
		)
			.then((response: any) => {
				if (response?.data) {
					dispatch(objectSucceeded(instanceId, response.data));
				} else {
					dispatch(objectSucceeded(instanceId, response));
				}
				if (callBack) {
					callBack(response, true);
				}
			})
			.catch((err) => {
				if (callBack) {
					callBack(err?.response, false);
				}
				return dispatch(objectFailed(instanceId));
			});
		
	}
};

export type RequestObjectType = {
	type: typeof OBJECT_REQUESTED;
	instanceId: Instances;
	id?: string;
	requestParams?: { [k: string]: string };
	filters?: { [k: string]: any };
};

export const requestObject = (
	instanceId: Instances,
	// enable id use case once it's available.
	// id?: string,
	requestParams?: { [k: string]: string },
	filters?: { [k: string]: any },
	type: string = 'get',
	includeId: boolean = true,
	header: boolean = true,
	data: object = undefined!,
	callBack?: any,
	id: string | undefined = undefined,
	apiService?: string,
	dynamicUrlData?: { [k: string]: string },
) => {
	return (dispatch: any) => {
		dispatch({
			type: OBJECT_REQUESTED,
			instanceId,
			requestParams,
			filters,
		});
		makeAPIcall(
			dispatch,
			instanceId,
			{ requestParams, filters },
			type,
			includeId,
			header,
			data,
			callBack,
			id,
			undefined,
			apiService,
			dynamicUrlData
		);
	};
};

export type ObjectSucceededType = {
	type: typeof OBJECT_SUCCEEDED;
	instanceId: string;
	object: any;
};

export const objectSucceeded = (
	instanceId: string,
	object: any
): ObjectSucceededType => ({
	type: OBJECT_SUCCEEDED,
	instanceId,
	object,
});

export type ObjectFailedType = {
	type: typeof OBJECT_FAILED;
	instanceId: string;
};

export const objectFailed = (instanceId: string): ObjectFailedType => ({
	type: OBJECT_FAILED,
	instanceId,
});

export type RequestLocalChangeObjectFieldType = {
	type: typeof LOCAL_CHANGE_OBJECT_FIELD_REQUESTED;
	instanceId: string;
	field: IObjectField;
	fieldsValueGetters: { [k: string]: (item: any) => any };
	merge: boolean;
};

export const requestLocalChangeObjectField = (
	instanceId: string,
	field: IObjectField,
	fieldsValueGetters: { [k: string]: (item: any) => any } = {},
	merge = false
): RequestLocalChangeObjectFieldType => ({
	type: LOCAL_CHANGE_OBJECT_FIELD_REQUESTED,
	instanceId,
	field,
	fieldsValueGetters,
	merge,
});

export type LocalCreateInitialObjectType = {
	type: typeof LOCAL_CREATE_INITIAL_OBJECT;
	instanceId: Instances;
	object: { [key: string]: any };
};

export const localCreateInitialObject = (
	instanceId: Instances,
	object: { [key: string]: any }
): LocalCreateInitialObjectType => ({
	type: LOCAL_CREATE_INITIAL_OBJECT,
	instanceId,
	object,
});

export type RemoveObjectType = {
	type: typeof REMOVE_OBJECT;
	instanceId: string;
};

export const removeObject = (instanceId: string): RemoveObjectType => ({
	type: REMOVE_OBJECT,
	instanceId,
});

export interface IChangeFilters<T> {
	type: typeof FILTERS_CHANGED;
	instanceId: Instances;
	filters: { [k: string]: T };
}

export const changeFilters = <T>(
	instanceId: Instances,
	filters: { [k: string]: T }
) => {
	return (
		dispatch: Dispatch<
			IChangeFilters<T> | ObjectSucceededType | ObjectFailedType
		>,
		getState: () => IStateType
	) => {
		dispatch({
			type: FILTERS_CHANGED,
			instanceId,
			filters,
		});

		const state = getState();
		makeAPIcall(dispatch, instanceId, {
			filters,
			requestParams: makeSelectRequestParams(instanceId)(state),
		});
	};
};

export type Action =
	| RequestObjectType
	| ObjectSucceededType
	| ObjectFailedType
	| RequestLocalChangeObjectFieldType
	| LocalCreateInitialObjectType
	| RemoveObjectType
	| IChangeFilters<unknown>;
